import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { startOfDay } from 'date-fns';
import { createTableSlice } from '../../../common/datagrid';
import { ErrorResponse, PriceDto, TableType } from '../../../common/PricePrgClient';
import { createAsyncAction, toDateString } from '../../../utils';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchWarehouses } from '../../stock/redux/stockSlices';
import { inactivatePriceTableInitialColumns } from '../InactivatePriceTableColumns';

export const getInactivatePriceKey = (price: Pick<PriceDto, 'productId' | 'pricelistId'>) =>
    `${price.productId}-${price.pricelistId}`;
export const deconstructPriceKey = (priceKey: string) => {
    const split = priceKey.split('-');
    return {
        productId: Number(split[0]),
        pricelistId: Number(split[1])
    };
};

const namespaces = {
    inactivatePrices: 'inactivatePrices',
    isLoading: 'isLoading',
    dialogs: 'dialogs',
    checkboxFilters: 'inactivatePriceCheckboxFilters'
};

type ActivatePriceRequest = {
    productId: number;
    pricelistId: number;
    isActive: boolean;
};

export const activatePrice = createAsyncAction<ActivatePriceRequest, number[], number[]>(
    namespaces.inactivatePrices,
    'activatePrice'
);

type FetchInactivatePricesRequest = {
    pricelistIds: number[];
    validFrom: string;
};
export const fetchInactivatePrices = createAsyncAction<
    FetchInactivatePricesRequest,
    PriceDto[],
    ErrorResponse
>(namespaces.inactivatePrices, 'fetchInactivatePrices');

type FetchInactivatePricesPartialRequest = FetchInactivatePricesRequest & { productIds: number[] };
export const fetchInactivatePricesPartial = createAsyncAction<
    FetchInactivatePricesPartialRequest,
    PriceDto[]
>(namespaces.inactivatePrices, 'fetchInactivatePricesPartial');

export const inactivatePricesTableFetchPricelistPeriodValidFroms = createAsyncAction<
    { pricelistIds: number[]; preselectDate: boolean },
    { validFroms: string[]; preselectDate: boolean }
>(namespaces.inactivatePrices, 'inactivatePriceTableFetchPricelistPeriodValidFroms');

const isLoadingSlice = createSlice({
    name: namespaces.isLoading,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchInactivatePrices.request, () => true)
            .addCase(fetchInactivatePrices.success, () => false)
            .addCase(fetchInactivatePrices.failure, () => false)
});

const dialogsSlice = createSlice({
    name: namespaces.dialogs,
    initialState: {
        isChoosePricelistsDialogOpen: false
    },
    reducers: {
        inactivatePriceTableOpenChoosePricelistDialog: state => {
            state.isChoosePricelistsDialogOpen = true;
        },
        inactivatePriceTableCloseChoosePricelistsDialog: state => {
            state.isChoosePricelistsDialogOpen = false;
        }
    }
});
export const {
    inactivatePriceTableOpenChoosePricelistDialog,
    inactivatePriceTableCloseChoosePricelistsDialog
} = dialogsSlice.actions;

type InactivePrice = Pick<PriceDto, 'priceId' | 'isActive' | 'agreementPriceIds'> & {
    isActiveToBeSaved?: boolean;
    isSaving?: boolean;
};

type InactivatePriceState = {
    selectedPricelistDate: string | null;
    selectedPricelistIds: number[];
    availablePricelistValidFromDates: string[];
    selectedWarehouse: {
        id: number | null;
        date: string;
    };
    prices: { [priceKey: string]: InactivePrice };
};
const inactivatePricesSlice = createSlice({
    name: namespaces.inactivatePrices,
    initialState: {
        selectedPricelistDate: null,
        selectedWarehouse: {
            id: null as number | null,
            date: toDateString(startOfDay(new Date()))
        },
        selectedPricelistIds: [],
        availablePricelistValidFromDates: [],
        prices: {}
    } as InactivatePriceState,
    reducers: {
        inactivatePricesTablePricelistIdsSelected: (state, action: PayloadAction<number[]>) => {
            state.selectedPricelistIds = action.payload;
        },
        inactivatePricesTablePricelistDateSelected: (state, action: PayloadAction<string>) => {
            state.selectedPricelistDate = action.payload;
        },
        inactivatePricesTableWarehouseSelected: (state, action: PayloadAction<number | null>) => {
            state.selectedWarehouse.id = action.payload;
        },
        inactivatePricesTableWarehouseDateSelected: (state, action: PayloadAction<string>) => {
            state.selectedWarehouse.date = toDateString(action.payload);
        },
        setPriceActiveStatusOptimistically: (
            state,
            action: PayloadAction<{ isActive: boolean; priceIdChildren: number[]; rootPriceId: number }>
        ) => {
            const { isActive, priceIdChildren, rootPriceId } = action.payload;

            const priceIds = [...priceIdChildren, rootPriceId];

            for (const price of Object.values(state.prices)) {
                if (price.priceId != null && priceIds.includes(price.priceId)) {
                    price.isActiveToBeSaved = isActive;
                    if (rootPriceId === price.priceId) {
                        price.isSaving = true;
                    }
                }
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchInactivatePrices.success, (state, action) => {
                const prices: {
                    [priceKey: string]: Pick<PriceDto, 'priceId' | 'isActive' | 'agreementPriceIds'>;
                } = {};
                for (const price of action.payload) {
                    prices[getInactivatePriceKey(price)] = {
                        isActive: price.isActive,
                        priceId: price.priceId,
                        agreementPriceIds: price.agreementPriceIds
                    };
                }
                state.prices = prices;
            })
            .addCase(fetchInactivatePricesPartial.success, (state, action) => {
                for (const price of action.payload) {
                    const priceKey = getInactivatePriceKey(price);

                    if (price.price === 0) {
                        delete state.prices[priceKey];
                    } else {
                        state.prices[priceKey] = {
                            isActive: price.isActive,
                            priceId: price.priceId,
                            agreementPriceIds: price.agreementPriceIds
                        };
                    }
                }
            })
            .addCase(inactivatePricesTableFetchPricelistPeriodValidFroms.success, (state, action) => {
                state.availablePricelistValidFromDates = action.payload.validFroms;
            })
            .addCase(activatePrice.success, (state, action) => {
                const priceIds = action.payload;

                for (const price of Object.values(state.prices)) {
                    if (price.priceId != null && priceIds.includes(price.priceId)) {
                        if (price.isActiveToBeSaved != null) {
                            price.isActive = price.isActiveToBeSaved;
                        }

                        delete price.isActiveToBeSaved;
                        delete price.isSaving;
                    }
                }
            })
            .addCase(activatePrice.failure, (state, action) => {
                const priceIds = action.payload;

                for (const price of Object.values(state.prices)) {
                    if (price.priceId != null && priceIds.includes(price.priceId)) {
                        delete price.isActiveToBeSaved;
                        delete price.isSaving;
                    }
                }
            })
            .addCase(fetchUserSettings.success, (state, { payload }) => {
                const tableSettings = payload?.tableSettings?.find(
                    ts => ts.table === TableType.InactivatePrices
                );

                if (tableSettings != null) {
                    state.selectedPricelistIds = tableSettings.tableColumnSettings
                        .filter(tcs => inactivatePriceTableInitialColumns[tcs.name] == null)
                        .map(tcs => Number(tcs.name))
                        .filter(num => !isNaN(num));
                }
            })
            .addCase(fetchWarehouses.success, (state, { payload }) => {
                if (payload.length > 0) {
                    const warehouseId = (payload.find(x => x.isPrimaryWarehouse) ?? payload[0])?.warehouseId;
                    if (warehouseId != null) {
                        state.selectedWarehouse.id = warehouseId;
                    }
                }
            })
});
export const {
    inactivatePricesTablePricelistIdsSelected,
    inactivatePricesTablePricelistDateSelected,
    inactivatePricesTableWarehouseSelected,
    inactivatePricesTableWarehouseDateSelected,
    setPriceActiveStatusOptimistically
} = inactivatePricesSlice.actions;

const initialPriceTableColumns = [
    'productId',
    'productName',
    'netWeight',
    'unit',
    'country',
    'purchaser',
    'stock'
].map(key => ({
    key: key,
    hide: false,
    width: undefined
}));

const tableSlice = createTableSlice(
    'inactivatePricesTable',
    {
        table: TableType.InactivatePrices,
        filters: {
            productId: '',
            productName: '',
            netWeight: '',
            unit: '',
            country: '',
            purchaser: '',
            stock: ''
        },
        showTooltips: true,
        sorting: {
            column: 'productId',
            direction: 'asc'
        },
        columns: initialPriceTableColumns
    },
    builder =>
        builder.addCase(inactivatePricesTablePricelistIdsSelected, (state, action) => {
            const selectedPricelistIds = action.payload;

            state.columns = [
                ...state.columns.filter(x => isNaN(Number(x.key))),
                ...selectedPricelistIds.map(s => ({ key: String(s), hide: false, width: undefined }))
            ].map(col => {
                const oldCol = state.columns.find(c => col.key === c.key);
                if (oldCol == null) {
                    return col;
                }

                return {
                    ...col,
                    ...oldCol
                };
            });
        })
);
export const { inactivatePricesTableActions } = tableSlice;

const checkBoxFiltersSlice = createSlice({
    name: namespaces.checkboxFilters,
    initialState: {
        showAllArticles: false,
        showInactiveArticlesOnly: false
    },
    reducers: {
        setInactivePriceTableShowAllArticles: (state, action: PayloadAction<boolean>) => {
            state.showAllArticles = action.payload;
        },
        setInactivePriceTableShowInactiveArticlesOnly: (state, action: PayloadAction<boolean>) => {
            state.showInactiveArticlesOnly = action.payload;
        }
    }
});
export const { setInactivePriceTableShowInactiveArticlesOnly, setInactivePriceTableShowAllArticles } =
    checkBoxFiltersSlice.actions;

export default combineReducers({
    prices: inactivatePricesSlice.reducer,
    ui: combineReducers({
        table: tableSlice.reducer,
        isLoading: isLoadingSlice.reducer,
        dialogs: dialogsSlice.reducer,
        checkboxFilters: checkBoxFiltersSlice.reducer
    })
});
